/**
 * Display labels the services build from server data (`services/http/mappers`, `lib/ics`, `lib/support`):
 * vehicle specs, price lines, refund tiers, places, durations. Server-provided text (names, descriptions,
 * agency rules, option titles) is never translated: it is shown as the server sends it.
 */
export const fr = {
  /* Fallback names */
  vehicle: 'Véhicule',
  experience: 'Expérience',
  customer: 'Client',
  partnerAgency: 'Agence partenaire',
  verifiedAgency: 'Agence partenaire vérifiée',
  verifiedAgencyIn: 'Agence partenaire vérifiée · {city}',
  verifiedProvider: 'Prestataire vérifié',
  blogCategory: 'Conseils',
  ticket: 'Billet',

  /* Vehicles */
  'transmission.manual': 'Manuelle',
  'transmission.automatic': 'Automatique',
  'fuel.diesel': 'Diesel',
  'fuel.petrol': 'Essence',
  'fuel.hybrid': 'Hybride',
  'fuel.electric': 'Électrique',
  availabilityConfirmed: 'Dispo confirmée',
  'mileage.perDay': '{km} km / jour',
  'mileage.extraCharged': 'Au-delà de {km} km par jour, chaque kilomètre est facturé {price} MAD par l’agence.',
  'mileage.included': 'Kilométrage inclus : {km} km par jour.',
  'mileage.unknown': 'Kilométrage selon l’agence',
  'included.mileage': 'Kilomètres supplémentaires facturés par l’agence',
  'included.cancel.title': 'Annulation gratuite',
  'included.cancel.text': 'Jusqu’à 48 h avant le départ',
  'included.support.title': 'Assistance 24/7',
  'included.support.text': 'Par chat et WhatsApp',
  'included.delivery.title': 'Livraison possible',
  'included.delivery.free': 'Gratuite, à votre adresse',
  'included.delivery.paid': 'À votre adresse, selon l’agence',
  'city.vehicles': { one: '{count} véhicule', other: '{count} véhicules' },
  'city.experiences': { one: '{count} expérience', other: '{count} expériences' },
  'agency.description.delivery':
    'Agence partenaire Cardnd à {city}. Réservez en ligne, l’agence confirme et vous remet le véhicule en agence ou à votre adresse.',
  'agency.description.agency':
    'Agence partenaire Cardnd à {city}. Réservez en ligne, l’agence confirme et vous remet le véhicule en agence.',

  /* Places of a trip */
  'place.departure': 'Au départ de {city}',
  'place.delivery': 'Livraison',
  'place.deliveryTo': 'Livraison · {address}',
  'place.agencyPickup': 'Récupération en agence · {city}',

  /* Vehicle price lines */
  'line.rental': 'Location',
  'line.rentalDays': 'Location · {days}',
  'line.longStay': 'Remise longue durée ({pct} %)',
  'line.delivery': 'Livraison à l’adresse',
  'line.agencyPickup': 'Récupération en agence',
  'line.free': 'Gratuite',
  'line.included': 'Incluse',
  /* Experience price lines */
  'line.group': { one: 'Groupe · {count} personne', other: 'Groupe · {count} personnes' },
  'line.option': 'Option',
  'line.pickup': 'Prise en charge',
  'line.adjustment': 'Ajustement',
  'line.seasonal': 'Tarif saisonnier',
  'participants.adult': 'Adultes',
  'participants.child': 'Enfants',
  'participants.infant': 'Bébés',
  'participants.free': { one: '{label} · {count} gratuit', other: '{label} · {count} gratuits' },
  'participants.paying.adult': { one: '{count} adulte payant', other: '{count} adultes payants' },
  'participants.paying.child': { one: '{count} enfant payant', other: '{count} enfants payants' },
  'participants.paying.infant': { one: '{count} bébé payant', other: '{count} bébés payants' },
  'participants.count.child': { one: '{count} enfant', other: '{count} enfants' },
  'participants.count.infant': { one: '{count} bébé', other: '{count} bébés' },

  /* Experiences */
  'difficulty.easy': 'Facile',
  'difficulty.moderate': 'Modérée',
  'difficulty.challenging': 'Exigeante',
  'difficulty.hard': 'Difficile',
  'cancellation.flexible': 'Annulation flexible',
  'cancellation.moderate': 'Annulation modérée',
  'cancellation.strict': 'Annulation stricte',
  'cancellation.hours': 'Remboursement intégral jusqu’à {hours} h avant',
  'cancellation.provider': 'Conditions d’annulation du prestataire',
  'duration.minutes': '{minutes} min',
  'duration.hours': '{hours} h',
  'duration.hoursMinutes': '{hours} h {minutes}',
  'group.private': 'Groupe privé',
  'group.upTo': 'Jusqu’à {count} pers.',
  'mode.circuit.private': 'Circuit privé',
  'mode.circuit.shared': 'Circuit partagé',
  'mode.transfer.private': 'Transfert privé',
  'mode.transfer.shared': 'Transfert partagé',
  'mode.excursion.private': 'Excursion privée',
  'mode.excursion.shared': 'Excursion partagée',

  /* Refund terms of a trip (activity cancellation terms in words) */
  'refundTerms.hours': 'Remboursement intégral jusqu’à {hours} h avant le départ, aucun remboursement ensuite.',
  'refundTerms.flexible': 'Remboursement intégral jusqu’à 24 h avant le départ, aucun remboursement ensuite.',
  'refundTerms.moderate':
    'Remboursement intégral jusqu’à 48 h avant le départ, 50 % jusqu’à 24 h avant, aucun remboursement ensuite.',
  'refundTerms.strict': 'Remboursement intégral jusqu’à 7 jours avant le départ, aucun remboursement ensuite.',
  'refundTerms.provider': 'Le remboursement suit les conditions d’annulation du prestataire.',
  /* Vehicle refund tiers (PAY-12) */
  'refund.untilStart': 'Jusqu’au départ',
  'refund.until': 'Jusqu’au {when}',
  'refund.after': 'Ensuite',
  'refund.lastDay': 'Moins de 24 h avant',
  'refund.full': 'Remboursement intégral',
  'refund.minusFee': 'Remboursé hors frais de service',
  'refund.half': '50 % remboursé',

  /* Chat threads */
  'thread.support': 'Support Cardnd',
  'thread.supportSubtitle': 'Assistance 24/7',
  'thread.yourBooking': 'Votre réservation',

  /* Calendar file and support message */
  'ics.summary': 'Location {title} · Cardnd',
  'ics.vehicle':
    'Réservation {ref}. Présentez votre bon QR et le code PIN à l’agence, avec votre permis et une pièce d’identité.',
  'ics.experience': 'Réservation {ref} · présentez votre bon QR au guide.',
  'support.whatsapp': "Bonjour CARDND, j'ai besoin d'aide avec ma demande de réservation {reference}.",
} as const;
